import { CampaignName, EmailType, PartnerOrganization } from '../API';
import { EngagementEmailType } from '../types/EngagementEmailType';
import getCampaignConfig, { CampaignConfig } from './getCampaignConfig';
import PartnerOrganizationGroup from './PartnerOrganizationGroup';

export enum BrandName {
  AETNA_BETTER_HEALTH_OF_ILLINOIS = 'AETNA_BETTER_HEALTH_OF_ILLINOIS',
  AMBETTER = 'AMBETTER', // TODO(86b2v39ge): rename this brand to be specific to NH Healthy Families and update Five9 routing
  AMBETTER_OHIO_BUCKEYE_HEALTH_PLAN = 'AMBETTER_OHIO_BUCKEYE_HEALTH_PLAN',
  ANTHEM_BLUE_CROSS = 'ANTHEM_BLUE_CROSS',
  ANTHEM_BLUE_CROSS_AND_BLUE_SHIELD = 'ANTHEM_BLUE_CROSS_AND_BLUE_SHIELD',
  ANTHEM_HEALTHKEEPERS = 'ANTHEM_HEALTHKEEPERS',
  ANTHEM_MAINEHEALTH = 'ANTHEM_MAINEHEALTH',
  BLUE_MEDICARE_ADVANTAGE = 'BLUE_MEDICARE_ADVANTAGE',
  HEALTHY_BLUE = 'HEALTHY_BLUE',
  HEALTHY_BLUE_MEDICARE = 'HEALTHY_BLUE_MEDICARE',
  WELLPOINT = 'WELLPOINT',
  WELLCARE_BY_ALLWELL = 'WELLCARE_BY_ALLWELL',
  WELLCARE_BY_HEALTH_NET = 'WELLCARE_BY_HEALTH_NET',
  HEALTH_NET_OF_CALIFORNIA = 'HEALTH_NET_OF_CALIFORNIA',
  WELLCARE_BY_HEALTH_NET_IN_PARTNERSHIP_WITH_CAL_VIVA = 'WELLCARE_BY_HEALTH_NET_IN_PARTNERSHIP_WITH_CAL_VIVA',
  WELLCARE = 'WELLCARE',
  WELLCARE_OF_KENTUCKY = 'WELLCARE_OF_KENTUCKY',
  MERIDIAN_COMPLETE_MEDICARE_MEDICAID_PLAN = 'MERIDIAN_COMPLETE_MEDICARE_MEDICAID_PLAN',
  MERIDIAN_MEDICAID_PLAN = 'MERIDIAN_MEDICAID_PLAN',
  BUCKEYE_HEALTH_PLAN = 'BUCKEYE_HEALTH_PLAN',
  BUCKEYE_HEALTH_PLAN_MYCARE_OHIO_MEDICARE_MEDICAID = 'BUCKEYE_HEALTH_PLAN_MYCARE_OHIO_MEDICARE_MEDICAID',
  SUPERIOR_HEALTH_PLAN_STAR_PLUS_MMP = 'SUPERIOR_HEALTH_PLAN_STAR_PLUS_MMP',
}

// Short links for landing pages using brand names
export const ANTHEM_BLUE_CROSS_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/anthem-blue-cross';
export const ANTHEM_BLUE_CROSS_AND_BLUE_SHIELD_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/anthem-blue-cross-and-blue-shield';
export const ANTHEM_HEALTHKEEPERS_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/anthem-healthkeepers';
export const ANTHEM_MAINEHEALTH_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/anthem-mainehealth';
export const BLUE_MEDICARE_ADVANTAGE_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/blue-medicare-advantage';
export const HEALTHY_BLUE_LANDING_PAGE_SHORT_LINK_EN = 'https://sprinter.health/healthy-blue';
export const HEALTHY_BLUE_MEDICARE_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/healthy-blue-medicare';
export const WELLPOINT_LANDING_PAGE_SHORT_LINK_EN = 'https://sprinter.health/wellpoint';

export const WELLCARE_LANDING_PAGE_SHORT_LINK_EN = 'https://sprinter.health/wellcare';
export const WELLCARE_HEALTH_NET_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/wellcare-healthnet';
export const WELLCARE_HEALTH_NET_CAL_VIVA_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/wellcare-healthnet-calviva';
export const WELLCARE_ALLWELL_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/wellcare-allwell';
export const WELLCARE_OF_KENTUCKY_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/wellcare-kentucky';
export const AMBETTER_NEWHAMPSHIRE_HEALTHY_FAMILIES_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/ambetter-nh';
export const AMBETTER_OHIO_BUCKEYE_HEALTH_PLAN_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/ambetter-oh';

export const BUCKEYE_MYCARE_LANDING_PAGE_SHORT_LINK_EN = 'https://sprinter.health/buckeye-mycare';
export const BUCKEYE_LANDING_PAGE_SHORT_LINK_EN = 'https://sprinter.health/buckeye';
export const MERIDIAN_COMPLETE_MEDICARE_MEDICAID_LANDING_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/meridiancomplete';

export const MERIDIAN_MEDICAID_LANDING_PAGE_SHORT_LINK_EN = 'https://sprinter.health/meridian';

export const SUPERIOR_HEALTH_PLAN_STAR_PLUS_MMP_REFERRAL_VERIFICATION_PAGE_SHORT_LINK_EN =
  'https://sprinter.health/superior-starplus-mmp';

interface Image {
  uri: string;
  width: number;
  height: number;
}

type ServiceSummaryCoverSheetConfig = {
  materialID: string; // This is an ID for the cover sheet which is used by external partners to track copy versions.
  tagline: string; // This is an tagline our partners can request to be in the care nav episode and encounter summaries cover sheets.
};

// This is using values from the `Telephonic` tab in the spreadsheet from Elevance
type EngagementOutreachEmailConfig = {
  cobrandingBannerImageUrl: string;
  tagline: string; // This is an tagline our partners can request to be in the care nav episode and encounter summaries cover sheets.
  materialIDByEmailTypeMap?: Record<EngagementEmailType, string | null>;
  nonDiscriminationNoticeUrl?: string;
};

type DirectMailConfig = {
  cobrandingBlackAndWhiteMailerLogo: string;
  materialID: string;
  formNumber: string; // ID for the mailer copy version which is used by external partners to track it in their own system
  brandCode: string; // Brand code (there may be multiple brand codes for a single brand, which is why we don't just use the brandCodes field)
  tagline: string; // This is an tagline our partners can request to be in the intro letter, care nav episode and encounter summaries cover sheets.
  coverPageInstructionsKey?: string; // Main text that appears on the branded cover page for patient encounter summaries
};

// TODO(Patients): add referral landing page and/or verification page URL paths here
// once we determine how we want to handle the pages across brands within the Elevance OH partner org.
export type BrandConfig = {
  brandCodes: Set<string>;
  cobrandingLogo?: Image; // These banners are exported at 2x so we need to divide the height and width by 2 when set
  directMailConfig?: DirectMailConfig;
  displayName: string;
  emailConfig: EngagementOutreachEmailConfig;
  fullBrandDisplayNameForMessaging: string;
  /** This is use in some SMS content to keep length under 160 characters. We set this to the fullBrandDisplayNameForMessaging
   * if we do not have explicit partner approval for the shortened version of the brand name.
   * Once we are approved to use shortened brand name, this field should be updated.
   * */
  shortBrandDisplayNameForMessaging: string;
  partnerOrganizationGroup: PartnerOrganizationGroup;
  partnerOrganizations: PartnerOrganization[];
  referralVerificationPageUrlSlug?: string;
  // TODO(86b0bawbh): update the return type to be
  // { [languageBCP47 in ReturnType<typeof getApprovedPatientLanguageWithoutRegionSubtagForMessaging>]: string }
  // once we move getApprovedPatientLanguageWithoutRegionSubtagForMessaging to shared.
  referralLandingPageUrlSlugs?: {
    [languageBCP47 in 'en' | 'es']: string;
  };
  referralLandingPageShortLinks?: {
    [languageBCP47 in 'en' | 'es']: string;
  };
  serviceSummaryCoverSheetConfig: ServiceSummaryCoverSheetConfig;
  partnerHelpURL?: null | string;
  shouldUseBrandedPatientEncounterSummaryCoverPage?: boolean; // this uses content from cobrandingLogo and directMailConfig
};

// TODO(https://app.clickup.com/t/86b0aaruv): Update the Brand Associated Partner Organizations for Cenene to only reference the specific partner organizations in the regions they support.
const CENTENE_PARTNER_ORGANIZATIONS = [
  PartnerOrganization.CENTENECA,
  PartnerOrganization.CENTENEMS,
  PartnerOrganization.CENTENEFL,
  PartnerOrganization.CENTENEMA,
  PartnerOrganization.CENTENEMI,
  PartnerOrganization.CENTENENH,
  PartnerOrganization.CENTENEOH,
  PartnerOrganization.CENTENEGA,
  PartnerOrganization.CENTENEKY,
  PartnerOrganization.CENTENEME,
  PartnerOrganization.CENTENEIN,
  PartnerOrganization.CENTENECT,
  PartnerOrganization.CENTENETX,
];

// TODO(Patients): verify the full & short names for each brand name with Sona,
// add the cobranding logos once we have them
export const BRAND_CONFIG: {
  [brandName in BrandName]: BrandConfig;
} = {
  [BrandName.ANTHEM_BLUE_CROSS]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/ABC.png',
      width: 276,
      height: 32,
    },
    displayName: 'Anthem Blue Cross',
    shortBrandDisplayNameForMessaging: 'Anthem',
    fullBrandDisplayNameForMessaging: 'Anthem Blue Cross',
    brandCodes: new Set(['ABC']),
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/ABC.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENABC_1001',
      brandCode: 'ABC',
      tagline:
        'Anthem Blue Cross is the trade name of: In California: Blue Cross of California, Anthem Blue Cross Partnership Plan, Anthem BC Health Insurance Company and Anthem Blue Cross Life and Health Insurance Company are independent licensees of the Blue Cross Association. In 11 northeastern counties of New York: Anthem Blue Cross is the trade name of Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc., Anthem Blue Cross Retiree Solutions and Anthem Blue Cross HP is the trade name of Anthem HP, LLC.  Independent licensees of the Blue Cross Association.  Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'MULTI-BC-CR-052767-24-CPN52733 March 2024',
      tagline:
        'Anthem Blue Cross is the trade name of: In California: Blue Cross of California and Blue Cross of California Partnership Plan, Inc. Anthem BC Health Insurance Company is the trade name of Anthem Insurance Companies, Inc. Blue Cross of California, Blue Cross of California Partnership Plan, Inc., Anthem Blue Cross Life and Health Insurance Company, and Anthem BC Health Insurance Company are independent licensees of the Blue Cross Association. In 11 northeastern counties of New York: Anthem HealthChoice Assurance, Inc. and Anthem HealthChoice HMO, Inc. In these same counties, Anthem Blue Cross Retiree Solutions is the trade name of Anthem Insurance Companies, Inc. Independent licensees of the Blue Cross Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/ABC.png',
      tagline:
        'Anthem Blue Cross is the trade name of: In California: Blue Cross of California, Anthem Blue Cross Partnership Plan, Anthem BC Health Insurance Company and Anthem Blue Cross Life and Health Insurance Company are independent licensees of the Blue Cross Association. In 11 northeastern counties of New York: Anthem Blue Cross is the trade name of Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc., Anthem Blue Cross Retiree Solutions and Anthem Blue Cross HP is the trade name of Anthem HP, LLC.  Independent licensees of the Blue Cross Association.  Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    referralVerificationPageUrlSlug: 'anthem-blue-cross',
    referralLandingPageUrlSlugs: {
      en: 'anthem-blue-cross',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: ANTHEM_BLUE_CROSS_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.ANTHEM_BLUE_CROSS_AND_BLUE_SHIELD]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/ABS.png',
      width: 300,
      height: 32,
    },
    displayName: 'Anthem Blue Cross and Blue Shield',
    shortBrandDisplayNameForMessaging: 'Anthem',
    fullBrandDisplayNameForMessaging: 'Anthem Blue Cross and Blue Shield',
    brandCodes: new Set(['ABS']),
    shouldUseBrandedPatientEncounterSummaryCoverPage: true,
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/ABS.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENABS_1002',
      brandCode: 'ABS',
      tagline:
        'Anthem Blue Cross and Blue Shield is the trade name of: In Colorado: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc. In Connecticut: Anthem Health Plans, Inc. In Georgia: Blue Cross Blue Shield Healthcare Plan of Georgia, Inc. In Indiana: Anthem Insurance Companies, Inc. In Kentucky: Anthem Health Plans of Kentucky, Inc. In Maine: Anthem Health Plans of Maine, Inc. In Missouri (excluding 30 counties in the Kansas City area): RightCHOICE® Managed Care, Inc. (RIT), Healthy Alliance® Life Insurance Company (HALIC), and HMO Missouri, Inc. RIT and certain affiliates administer non-HMO benefits underwritten by HALIC and HMO benefits underwritten by HMO Missouri, Inc. RIT and certain affiliates only provide administrative services for self-funded plans and do not underwrite benefits. In Nevada: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc., dba HMO Nevada. In New Hampshire: Anthem Health Plans of New Hampshire, Inc. HMO plans are administered by Anthem Health Plans of New Hampshire, Inc. and underwritten by Matthew Thornton Health Plan, Inc. In 17 southeastern counties of New York:  Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc.  In these same counties Anthem Blue Cross and Blue Shield HP is the trade name of Anthem HP, LLC and Anthem Insurance Companies, Inc., dba Anthem Blue Cross and Blue Shield Retiree Solutions. In Ohio: Community Insurance Company. In Virginia: Anthem Health Plans of Virginia, Inc. trades as Anthem Blue Cross and Blue Shield in Virginia with its affiliate Healthkeepers, Inc., and its service area is all of Virginia except for the City of Fairfax, the Town of Vienna, and the area east of State Route 123. In Wisconsin: Blue Cross Blue Shield of Wisconsin (BCBSWI), underwrites or administers PPO and indemnity policies and underwrites the out of network benefits in POS policies offered by Compcare Health Services Insurance Corporation (Compcare) or Wisconsin Collaborative Insurance Corporation (WCIC). Compcare underwrites or administers HMO or POS policies; WCIC underwrites or administers Well Priority HMO or POS policies. Independent licensees of the Blue Cross Blue Shield Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
      coverPageInstructionsKey:
        'patientEncounterSummary:instructionsSectionCompleteInstructionsText|ABS',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'MULTI-BCBS-CR-052768-24-CPN52733 March 2024',
      tagline:
        'Anthem Blue Cross and Blue Shield is the trade name of: In Colorado: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc. In Connecticut: Anthem Health Plans, Inc. In Georgia: Blue Cross Blue Shield Healthcare Plan of Georgia, Inc. In Indiana: Anthem Insurance Companies, Inc. In Kentucky: Anthem Health Plans of Kentucky, Inc. In Maine: Anthem Health Plans of Maine, Inc. In Missouri (excluding 30 counties in the Kansas City area): RightCHOICE® Managed Care, Inc. (RIT), Healthy Alliance® Life Insurance Company (HALIC), and HMO Missouri, Inc. RIT and certain affiliates administer non-HMO benefits underwritten by HALIC and HMO benefits underwritten by HMO Missouri, Inc. RIT and certain affiliates only provide administrative services for self-funded plans and do not underwrite benefits. In Nevada: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc., dba HMO Nevada. In New Hampshire: Anthem Health Plans of New Hampshire, Inc. HMO plans are administered by Anthem Health Plans of New Hampshire, Inc. and underwritten by Matthew Thornton Health Plan, Inc. In 17 southeastern counties of New York: Anthem HealthChoice Assurance, Inc. and Anthem HealthChoice HMO, Inc. In these same counties, Anthem Blue Cross and Blue Shield HP is the trade name of Anthem HP, LLC and Anthem Blue Cross and Blue Shield Retiree Solutions is the trade name of Anthem Insurance Companies, Inc. In Ohio: Community Insurance Company. In Virginia: Anthem Health Plans of Virginia, Inc. Anthem Blue Cross and Blue Shield and its affiliate Healthkeepers, Inc. serve all of Virginia except for the City of Fairfax, the Town of Vienna, and the area east of State Route 123. In Wisconsin: Blue Cross Blue Shield of Wisconsin (BCBSWI). BCBSWI underwrites or administers PPO and indemnity policies and underwrites the out of network benefits in POS policies offered by Compcare Health Services Insurance Corporation (Compcare) or Wisconsin Collaborative Insurance Corporation (WCIC). Compcare underwrites or administers HMO or POS policies; WCIC underwrites or administers Well Priority HMO or POS policies. Independent licensees of the Blue Cross Blue Shield Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/ABS.png',
      tagline:
        'Anthem Blue Cross and Blue Shield is the trade name of: In Colorado: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc. In Connecticut: Anthem Health Plans, Inc. In Georgia: Blue Cross Blue Shield Healthcare Plan of Georgia, Inc. In Indiana: Anthem Insurance Companies, Inc. In Kentucky: Anthem Health Plans of Kentucky, Inc. In Maine: Anthem Health Plans of Maine, Inc. In Missouri (excluding 30 counties in the Kansas City area): RightCHOICE® Managed Care, Inc. (RIT), Healthy Alliance® Life Insurance Company (HALIC), and HMO Missouri, Inc. RIT and certain affiliates administer non-HMO benefits underwritten by HALIC and HMO benefits underwritten by HMO Missouri, Inc. RIT and certain affiliates only provide administrative services for self-funded plans and do not underwrite benefits. In Nevada: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc., dba HMO Nevada. In New Hampshire: Anthem Health Plans of New Hampshire, Inc. HMO plans are administered by Anthem Health Plans of New Hampshire, Inc. and underwritten by Matthew Thornton Health Plan, Inc. In 17 southeastern counties of New York:  Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc.  In these same counties Anthem Blue Cross and Blue Shield HP is the trade name of Anthem HP, LLC and Anthem Insurance Companies, Inc., dba Anthem Blue Cross and Blue Shield Retiree Solutions. In Ohio: Community Insurance Company. In Virginia: Anthem Health Plans of Virginia, Inc. trades as Anthem Blue Cross and Blue Shield in Virginia with its affiliate Healthkeepers, Inc., and its service area is all of Virginia except for the City of Fairfax, the Town of Vienna, and the area east of State Route 123. In Wisconsin: Blue Cross Blue Shield of Wisconsin (BCBSWI), underwrites or administers PPO and indemnity policies and underwrites the out of network benefits in POS policies offered by Compcare Health Services Insurance Corporation (Compcare) or Wisconsin Collaborative Insurance Corporation (WCIC). Compcare underwrites or administers HMO or POS policies; WCIC underwrites or administers Well Priority HMO or POS policies. Independent licensees of the Blue Cross Blue Shield Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    referralVerificationPageUrlSlug: 'anthem-blue-cross-and-blue-shield',
    referralLandingPageUrlSlugs: {
      en: 'anthem-blue-cross-and-blue-shield',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: ANTHEM_BLUE_CROSS_AND_BLUE_SHIELD_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.ANTHEM_HEALTHKEEPERS]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/AHKM.png',
      width: 268,
      height: 32,
    },
    displayName: 'Anthem Healthkeepers',
    shortBrandDisplayNameForMessaging: 'Anthem',
    fullBrandDisplayNameForMessaging: 'Anthem Healthkeepers',
    brandCodes: new Set(['AHKM']),
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/AHKM.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENAHK_1008',
      brandCode: 'AHKM',
      tagline:
        'Anthem Blue Cross and Blue Shield is the trade name of: In Colorado: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc. In Connecticut: Anthem Health Plans, Inc. In Georgia: Blue Cross Blue Shield Healthcare Plan of Georgia, Inc. In Indiana: Anthem Insurance Companies, Inc. In Kentucky: Anthem Health Plans of Kentucky, Inc. In Maine: Anthem Health Plans of Maine, Inc. In Missouri (excluding 30 counties in the Kansas City area): RightCHOICE® Managed Care, Inc. (RIT), Healthy Alliance® Life Insurance Company (HALIC), and HMO Missouri, Inc. RIT and certain affiliates administer non-HMO benefits underwritten by HALIC and HMO benefits underwritten by HMO Missouri, Inc. RIT and certain affiliates only provide administrative services for self-funded plans and do not underwrite benefits. In Nevada: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc., dba HMO Nevada. In New Hampshire: Anthem Health Plans of New Hampshire, Inc. HMO plans are administered by Anthem Health Plans of New Hampshire, Inc. and underwritten by Matthew Thornton Health Plan, Inc. In 17 southeastern counties of New York:  Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc.  In these same counties Anthem Blue Cross and Blue Shield HP is the trade name of Anthem HP, LLC and Anthem Insurance Companies, Inc., dba Anthem Blue Cross and Blue Shield Retiree Solutions. In Ohio: Community Insurance Company. In Virginia: Anthem Health Plans of Virginia, Inc. trades as Anthem Blue Cross and Blue Shield in Virginia with its affiliate Healthkeepers, Inc., and its service area is all of Virginia except for the City of Fairfax, the Town of Vienna, and the area east of State Route 123. In Wisconsin: Blue Cross Blue Shield of Wisconsin (BCBSWI), underwrites or administers PPO and indemnity policies and underwrites the out of network benefits in POS policies offered by Compcare Health Services Insurance Corporation (Compcare) or Wisconsin Collaborative Insurance Corporation (WCIC). Compcare underwrites or administers HMO or POS policies; WCIC underwrites or administers Well Priority HMO or POS policies. Independent licensees of the Blue Cross Blue Shield Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      tagline:
        'Anthem Blue Cross and Blue Shield is the trade name of: In Colorado: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc. In Connecticut: Anthem Health Plans, Inc. In Georgia: Blue Cross Blue Shield Healthcare Plan of Georgia, Inc. In Indiana: Anthem Insurance Companies, Inc. In Kentucky: Anthem Health Plans of Kentucky, Inc. In Maine: Anthem Health Plans of Maine, Inc. In Missouri (excluding 30 counties in the Kansas City area): RightCHOICE® Managed Care, Inc. (RIT), Healthy Alliance® Life Insurance Company (HALIC), and HMO Missouri, Inc. RIT and certain affiliates administer non-HMO benefits underwritten by HALIC and HMO benefits underwritten by HMO Missouri, Inc. RIT and certain affiliates only provide administrative services for self-funded plans and do not underwrite benefits. In Nevada: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc., dba HMO Nevada. In New Hampshire: Anthem Health Plans of New Hampshire, Inc. HMO plans are administered by Anthem Health Plans of New Hampshire, Inc. and underwritten by Matthew Thornton Health Plan, Inc. In 17 southeastern counties of New York:  Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc.  In these same counties Anthem Blue Cross and Blue Shield HP is the trade name of Anthem HP, LLC and Anthem Insurance Companies, Inc., dba Anthem Blue Cross and Blue Shield Retiree Solutions. In Ohio: Community Insurance Company. In Virginia: Anthem Health Plans of Virginia, Inc. trades as Anthem Blue Cross and Blue Shield in Virginia with its affiliate Healthkeepers, Inc., and its service area is all of Virginia except for the City of Fairfax, the Town of Vienna, and the area east of State Route 123. In Wisconsin: Blue Cross Blue Shield of Wisconsin (BCBSWI), underwrites or administers PPO and indemnity policies and underwrites the out of network benefits in POS policies offered by Compcare Health Services Insurance Corporation (Compcare) or Wisconsin Collaborative Insurance Corporation (WCIC). Compcare underwrites or administers HMO or POS policies; WCIC underwrites or administers Well Priority HMO or POS policies. Independent licensees of the Blue Cross Blue Shield Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/AHKM.png',
      tagline:
        'Anthem Blue Cross and Blue Shield is the trade name of: In Colorado: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc. In Connecticut: Anthem Health Plans, Inc. In Georgia: Blue Cross Blue Shield Healthcare Plan of Georgia, Inc. In Indiana: Anthem Insurance Companies, Inc. In Kentucky: Anthem Health Plans of Kentucky, Inc. In Maine: Anthem Health Plans of Maine, Inc. In Missouri (excluding 30 counties in the Kansas City area): RightCHOICE® Managed Care, Inc. (RIT), Healthy Alliance® Life Insurance Company (HALIC), and HMO Missouri, Inc. RIT and certain affiliates administer non-HMO benefits underwritten by HALIC and HMO benefits underwritten by HMO Missouri, Inc. RIT and certain affiliates only provide administrative services for self-funded plans and do not underwrite benefits. In Nevada: Rocky Mountain Hospital and Medical Service, Inc. HMO products underwritten by HMO Colorado, Inc., dba HMO Nevada. In New Hampshire: Anthem Health Plans of New Hampshire, Inc. HMO plans are administered by Anthem Health Plans of New Hampshire, Inc. and underwritten by Matthew Thornton Health Plan, Inc. In 17 southeastern counties of New York:  Anthem HealthChoice Assurance, Inc., and Anthem HealthChoice HMO, Inc.  In these same counties Anthem Blue Cross and Blue Shield HP is the trade name of Anthem HP, LLC and Anthem Insurance Companies, Inc., dba Anthem Blue Cross and Blue Shield Retiree Solutions. In Ohio: Community Insurance Company. In Virginia: Anthem Health Plans of Virginia, Inc. trades as Anthem Blue Cross and Blue Shield in Virginia with its affiliate Healthkeepers, Inc., and its service area is all of Virginia except for the City of Fairfax, the Town of Vienna, and the area east of State Route 123. In Wisconsin: Blue Cross Blue Shield of Wisconsin (BCBSWI), underwrites or administers PPO and indemnity policies and underwrites the out of network benefits in POS policies offered by Compcare Health Services Insurance Corporation (Compcare) or Wisconsin Collaborative Insurance Corporation (WCIC). Compcare underwrites or administers HMO or POS policies; WCIC underwrites or administers Well Priority HMO or POS policies. Independent licensees of the Blue Cross Blue Shield Association. Anthem is a registered trademark of Anthem Insurance Companies, Inc.',
    },
    referralVerificationPageUrlSlug: 'anthem-healthkeepers',
    referralLandingPageUrlSlugs: {
      en: 'anthem-healthkeepers',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: ANTHEM_HEALTHKEEPERS_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.ANTHEM_MAINEHEALTH]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/AMHM.png',
      width: 332,
      height: 32,
    },
    displayName: 'Anthem MaineHealth',
    shortBrandDisplayNameForMessaging: 'Anthem',
    fullBrandDisplayNameForMessaging: 'Anthem MaineHealth',
    brandCodes: new Set(['AMHM']),
    shouldUseBrandedPatientEncounterSummaryCoverPage: true,
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/AMHM.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENAMH_1004',
      brandCode: 'AMHM',
      tagline:
        'Plans offered by AMH Health, LLC, a joint venture between MaineHealth and Anthem Partnership Holding Company, LLC. Plans offered by AMH Health Plans Of Maine, Inc, a joint venture between MaineHealth and Anthem Partnership Holding Company, LLC. AMH Health, LLC and AMH Health Plans Of Maine, Inc are independent licensees of the Blue Cross Blue Shield Association.',
      coverPageInstructionsKey:
        'patientEncounterSummary:instructionsSectionCompleteInstructionsText|AMHM',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'MEAMH-CR-052772-24-CPN52733 March 2024',
      tagline:
        'Plans offered by AMH Health, LLC., a joint venture between MaineHealth and Anthem Partnership Holding Company, LLC. AMH Health is an independent licensee of the Blue Cross Blue Shield Association.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/AMHM.png',
      tagline:
        'Plans offered by AMH Health, LLC, a joint venture between MaineHealth and Anthem Partnership Holding Company, LLC. Plans offered by AMH Health Plans Of Maine, Inc, a joint venture between MaineHealth and Anthem Partnership Holding Company, LLC. AMH Health, LLC and AMH Health Plans Of Maine, Inc are independent licensees of the Blue Cross Blue Shield Association.',
    },
    referralVerificationPageUrlSlug: 'anthem-mainehealth',
    referralLandingPageUrlSlugs: {
      en: 'anthem-mainehealth',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: ANTHEM_MAINEHEALTH_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.BLUE_MEDICARE_ADVANTAGE]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/BMAG.png',
      width: 300,
      height: 32,
    },
    displayName: 'Blue Medicare Advantage',
    fullBrandDisplayNameForMessaging: 'Blue Medicare Advantage',
    shortBrandDisplayNameForMessaging: 'Blue Medicare Advantage', // There is no short brand name for messaging, so we use the same as the full brand name
    brandCodes: new Set(['BMAG']),
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/BMAG.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENBMP_1007',
      brandCode: 'BMAG',
      tagline:
        'Blue Medicare Advantage is the trade name of Group Retiree Health Solutions, Inc. an independent licensee of the Blue Cross and Blue Shield Association.',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'PAIBC-CR-052777-24-CPN52733 March 2024',
      tagline:
        'Blue Medicare Advantage is the trade name of Group Retiree Health Solutions, Inc., an independent licensee of the Blue Cross Blue Shield Association.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/BMAG.png',
      tagline:
        'Blue Medicare Advantage is the trade name of Group Retiree Health Solutions, Inc. an independent licensee of the Blue Cross and Blue Shield Association.',
    },
    referralVerificationPageUrlSlug: 'blue-medicare-advantage',
    referralLandingPageUrlSlugs: {
      en: 'blue-medicare-advantage',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: BLUE_MEDICARE_ADVANTAGE_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.HEALTHY_BLUE]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/HBLM.png',
      width: 300,
      height: 32,
    },
    displayName: 'Healthy Blue',
    fullBrandDisplayNameForMessaging: 'Healthy Blue',
    shortBrandDisplayNameForMessaging: 'Healthy Blue', // There is no short brand name for messaging, so we use the same as the full brand name
    brandCodes: new Set(['HBLM']),
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/HBLM.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENHBL_1009',
      brandCode: 'HBLM',
      tagline:
        'Healthy Blue is the trade name of Community Care Health Plan of Louisiana, Inc., an independent licensee of the Blue Cross Blue Shield Association.',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'LAHB-CR-052771-24 CPN52733 March 2024',
      tagline:
        'Healthy Blue is the trade name of Community Care Health Plan of Louisiana, Inc., an independent licensee of the Blue Cross Blue Shield Association.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/HBLM.png',
      tagline:
        'Healthy Blue is the trade name of Community Care Health Plan of Louisiana, Inc., an independent licensee of the Blue Cross Blue Shield Association.',
    },
    referralVerificationPageUrlSlug: 'healthy-blue',
    referralLandingPageUrlSlugs: {
      en: 'healthy-blue',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: HEALTHY_BLUE_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.HEALTHY_BLUE_MEDICARE]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/BNCM.png',
      width: 332,
      height: 32,
    },
    displayName: 'Healthy Blue + Medicare',
    fullBrandDisplayNameForMessaging: 'Healthy Blue + Medicare',
    shortBrandDisplayNameForMessaging: 'Healthy Blue + Medicare', // There is no short brand name for messaging, so we use the same as the full brand name
    brandCodes: new Set(['BNCM']),
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/BNCM.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENBNC_1005',
      brandCode: 'BNCM',
      tagline:
        'Blue Cross and Blue Shield of North Carolina is an independent licensee of the Blue Cross and Blue Shield Association. ® Marks of the Blue Cross and Blue Shield Association.',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      tagline:
        'Blue Cross and Blue Shield of North Carolina is an independent licensee of the Blue Cross and Blue Shield Association. ® Marks of the Blue Cross and Blue Shield Association.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/BNCM.png',
      tagline:
        'Blue Cross and Blue Shield of North Carolina is an independent licensee of the Blue Cross and Blue Shield Association. ® Marks of the Blue Cross and Blue Shield Association.',
    },
    referralVerificationPageUrlSlug: 'healthy-blue-medicare',
    referralLandingPageUrlSlugs: {
      en: 'healthy-blue-medicare',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: HEALTHY_BLUE_MEDICARE_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.WELLPOINT]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.ELEVANCEHEALTH,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/elevancehealth/email/WPT.png',
      width: 256,
      height: 32,
    },
    displayName: 'Wellpoint',
    shortBrandDisplayNameForMessaging: 'Wellpoint',
    fullBrandDisplayNameForMessaging: 'Wellpoint',
    brandCodes: new Set(['WPT']),
    directMailConfig: {
      cobrandingBlackAndWhiteMailerLogo:
        'https://app.sprinterhealth.com/elevancehealth/mailer/bw/WPT.png',
      materialID: 'Y0114_24_3007650_0000_I_C 03/06/2024',
      formNumber: '1065426MUSENWLP_1003',
      brandCode: 'WPT',
      tagline:
        'In Arizona: services are provided by Wellpoint Texas, Inc., Wellpoint Ohio, Inc., or Wellpoint Insurance Company. In Iowa: services are provided by Wellpoint Iowa, Inc. In New Jersey: services are provided by Wellpoint New Jersey, Inc. or Wellpoint Insurance Company. In New Mexico: services are provided by Wellpoint Community Care of New Mexico, Inc., In Tennessee: services are provided by Wellpoint Texas, Inc. or Wellpoint Tennessee, Inc. In Texas: services are provided by Wellpoint Texas, Inc. or Wellpoint Insurance Company. In Washington: services provided by Wellpoint Washington, Inc.',
    },
    serviceSummaryCoverSheetConfig: {
      materialID: 'MULTI-WP-CR-052769-24-CPN52733 March 2024',
      tagline:
        'Services provided by: In Arizona: Wellpoint Texas, Inc., Wellpoint Ohio, Inc., or Wellpoint Insurance Company. In Iowa: Wellpoint Iowa, Inc. In New Jersey: Wellpoint New Jersey, Inc. or Wellpoint Insurance Company. In Tennessee: Wellpoint Tennessee, Inc. or Wellpoint Insurance Company. In Texas: Wellpoint Texas, Inc. or Wellpoint Insurance Company. In Washington: Wellpoint Washington, Inc.',
    },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/elevancehealth/email/WPT.png',
      tagline:
        'In Arizona: services are provided by Wellpoint Texas, Inc., Wellpoint Ohio, Inc., or Wellpoint Insurance Company. In Iowa: services are provided by Wellpoint Iowa, Inc. In New Jersey: services are provided by Wellpoint New Jersey, Inc. or Wellpoint Insurance Company. In New Mexico: services are provided by Wellpoint Community Care of New Mexico, Inc., In Tennessee: services are provided by Wellpoint Texas, Inc. or Wellpoint Tennessee, Inc. In Texas: services are provided by Wellpoint Texas, Inc. or Wellpoint Insurance Company. In Washington: services provided by Wellpoint Washington, Inc.',
    },
    referralVerificationPageUrlSlug: 'wellpoint',
    referralLandingPageUrlSlugs: {
      en: 'wellpoint',
      es: '',
    },
    referralLandingPageShortLinks: {
      en: WELLPOINT_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.ELEVANCEHEALTH],
  },
  [BrandName.WELLCARE_BY_ALLWELL]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_by_allwell.png',
      width: 236,
      height: 32,
    },
    displayName: 'Wellcare By Allwell',
    shortBrandDisplayNameForMessaging: 'Wellcare',
    fullBrandDisplayNameForMessaging: 'Wellcare By Allwell',
    brandCodes: new Set([
      'H4343',
      'H9630',
      'H0351',
      'H5590',
      'H8225',
      'H7399',
      'H1774',
      'H3499',
      'H6348',
      'H7925',
      'H5398',
      'H6550',
      'H6830',
      'H9387',
      'H0482',
      'H1664',
      'H2134',
      'H9976',
      'H6446',
      'H8458',
      'H0908',
      'H7169',
      'H2128',
      'H2915',
      'H5294',
      'H6678',
      'H9357',
      'H8189',
    ]),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_by_allwell.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'Y0020_WCM_142318E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'Y0020_WCM_144432E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'Y0020_WCM_144433E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'Y0020_WCM_144434E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'Y0020_WCM_144435E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: 'Y0020_WCM_144436E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'Y0020_WCM_144437E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
    },
    referralVerificationPageUrlSlug: 'wellcare-allwell',
    referralLandingPageUrlSlugs: { en: 'wellcare-allwell', es: '' },
    referralLandingPageShortLinks: {
      en: WELLCARE_ALLWELL_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
  },
  [BrandName.WELLCARE_BY_HEALTH_NET]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_by_health_net.png',
      width: 236,
      height: 32,
    },
    displayName: 'Wellcare By Health Net',
    shortBrandDisplayNameForMessaging: 'Wellcare',
    fullBrandDisplayNameForMessaging: 'Wellcare By Health Net',
    brandCodes: new Set([
      'H7360',
      'H5439',
      'H6815',
      'H0562',
      'H3561-001',
      'H3561-008',
      'H3561-009',
    ]),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_by_health_net.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'H3561_CNC_147985E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'H3561_CNC_147986E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'H3561_CNC_147987E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'H3561_CNC_147988E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'H3561_CNC_147989E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: 'H3561_CNC_147990E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'H3561_CNC_147991E_C',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
      nonDiscriminationNoticeUrl:
        'https://wellcare.healthnetcalifornia.com/legal/nondiscrimination-notice.html',
    },
    referralVerificationPageUrlSlug: 'wellcare-healthnet',
    referralLandingPageUrlSlugs: { en: 'wellcare-healthnet', es: '' },
    referralLandingPageShortLinks: {
      en: WELLCARE_HEALTH_NET_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
  },
  [BrandName.HEALTH_NET_OF_CALIFORNIA]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/health_net_of_california.png',
      width: 228,
      height: 32,
    },
    displayName: 'Health Net of California',
    fullBrandDisplayNameForMessaging: 'Health Net of California',
    shortBrandDisplayNameForMessaging: 'Health Net',
    brandCodes: new Set(['HNCA']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/health_net_of_california.png',
      tagline:
        'Health Net Community Solutions, Inc. is a subsidiary of Health Net, LLC. Health Net is a registered service mark of Health Net, LLC. All other identified trademarks/service marks remain the property of their respective companies. All rights reserved.',
    },
    referralVerificationPageUrlSlug: 'health-net-of-california',
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
  },
  [BrandName.WELLCARE_BY_HEALTH_NET_IN_PARTNERSHIP_WITH_CAL_VIVA]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri:
        'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_by_health_net_in_partnership_with_cal_viva.png',
      width: 236,
      height: 32,
    },
    displayName: 'Wellcare By Health Net in Partnership with CalViva Health',
    fullBrandDisplayNameForMessaging: 'Wellcare By Health Net in Partnership with CalViva Health',
    shortBrandDisplayNameForMessaging: 'Wellcare',
    brandCodes: new Set(['H3561-007']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_by_health_net_in_partnership_with_cal_viva.png',
      tagline: '',
      nonDiscriminationNoticeUrl:
        'https://wellcare.healthnetcalifornia.com/legal/nondiscrimination-notice.html',
    },
    referralVerificationPageUrlSlug: 'wellcare-healthnet-calviva',
    referralLandingPageUrlSlugs: { en: 'wellcare-healthnet-calviva', es: '' },
    referralLandingPageShortLinks: {
      en: WELLCARE_HEALTH_NET_CAL_VIVA_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
  },
  [BrandName.WELLCARE]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/wellcare.png',
      width: 256,
      height: 32,
    },
    displayName: 'Wellcare',
    fullBrandDisplayNameForMessaging: 'Wellcare',
    shortBrandDisplayNameForMessaging: 'Wellcare', // There is no short brand name for messaging, so we use the same as the full brand name
    brandCodes: new Set([
      'H4661',
      'H1215',
      'H1395',
      'H4537',
      'H9900',
      'H1416',
      'H1848',
      'H6975',
      'H0270',
      'H8553',
      'H5087',
      'H0712',
      'H1914',
      'H1032',
      'H5199',
      'H0111',
      'H1112',
      'H5779',
      'H6713',
      'H3975',
      'H9730',
      'H3047',
      'H6193',
      'H9761',
      'H9364',
      'H2117',
      'H5475',
      'H7518',
      'H9335',
      'H0074',
      'H4073',
      'H7175',
      'H0969',
      'H2162',
      'H0913',
      'H8711',
      'H2775',
      'H2816',
      'H0088',
      'H4868',
      'H4699',
      'H9258',
      'H4847',
      'H7326',
      'H9428',
      'H0174',
      'H4506',
      'H7323',
      'H1862',
      'H6594',
      'H1353',
      'H5965',
    ]),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/centene/cobranding64h/wellcare.png',
      tagline: '',
    },
    referralVerificationPageUrlSlug: 'wellcare',
    referralLandingPageUrlSlugs: { en: 'wellcare', es: '' },
    referralLandingPageShortLinks: {
      en: WELLCARE_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
  },

  [BrandName.WELLCARE_OF_KENTUCKY]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_of_kentucky.png',
      width: 256,
      height: 32,
    },
    displayName: 'WellCare of Kentucky',
    fullBrandDisplayNameForMessaging: 'WellCare of Kentucky',
    shortBrandDisplayNameForMessaging: 'WellCare',
    brandCodes: new Set(['KYMCD']), // hcontract from gap list
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/wellcare_of_kentucky.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: 'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'KY4CADEML63992E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
    },
    referralVerificationPageUrlSlug: 'wellcare-kentucky',
    referralLandingPageUrlSlugs: { en: 'wellcare-kentucky', es: '' },
    referralLandingPageShortLinks: {
      en: WELLCARE_OF_KENTUCKY_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.CENTENEKY],
  },

  [BrandName.MERIDIAN_COMPLETE_MEDICARE_MEDICAID_PLAN]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri:
        'https://app.sprinterhealth.com/centene/cobranding64h/meridian_complete_medicare_medicaid_plan.png',
      width: 332,
      height: 32,
    },
    displayName: 'MeridianComplete (Medicare-Medicaid Plan)',
    fullBrandDisplayNameForMessaging: 'MeridianComplete (Medicare-Medicaid Plan)',
    shortBrandDisplayNameForMessaging: 'MeridianComplete',
    brandCodes: new Set(['H0480']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    referralVerificationPageUrlSlug: 'meridiancomplete',
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/meridian_complete_medicare_medicaid_plan.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'H0480_MMP_148553E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'H0480_MMP_148559E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'H0480_MMP_148554E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'H0480_MMP_148555E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'H0480_MMP_148556E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'H0480_MMP_148557E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: 'H0480_MMP_148558E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
    },
    referralLandingPageUrlSlugs: { en: 'meridiancomplete', es: '' },
    referralLandingPageShortLinks: {
      en: MERIDIAN_COMPLETE_MEDICARE_MEDICAID_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.CENTENEMI],
    partnerHelpURL: 'https://mmp.mimeridian.com/contact-us.html',
  },

  [BrandName.MERIDIAN_MEDICAID_PLAN]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/meridian_medicaid_plan.png',
      width: 332,
      height: 32,
    },
    displayName: 'Meridian Health Plan',
    fullBrandDisplayNameForMessaging: 'Meridian Health Plan',
    shortBrandDisplayNameForMessaging: 'Meridian',
    brandCodes: new Set(['MERIDIANHP']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    referralVerificationPageUrlSlug: 'meridian',
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/meridian_medicaid_plan.png',
      tagline: '',
    },
    referralLandingPageUrlSlugs: { en: 'meridian', es: '' },
    referralLandingPageShortLinks: {
      en: MERIDIAN_MEDICAID_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.CENTENEMI],
  },

  [BrandName.BUCKEYE_HEALTH_PLAN]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/buckeye_health_plan.png',
      width: 268,
      height: 32,
    },
    displayName: 'Buckeye Health Plan',
    fullBrandDisplayNameForMessaging: 'Buckeye Health Plan',
    shortBrandDisplayNameForMessaging: 'Buckeye',
    brandCodes: new Set(['BHPDEFAULT']), // default brand code in our system
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    referralVerificationPageUrlSlug: 'buckeye',
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
    referralLandingPageUrlSlugs: { en: 'buckeye', es: '' },
    referralLandingPageShortLinks: { en: BUCKEYE_LANDING_PAGE_SHORT_LINK_EN, es: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/buckeye_health_plan.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: 'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'BHP-MM-071624',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
    },
  },
  [BrandName.BUCKEYE_HEALTH_PLAN_MYCARE_OHIO_MEDICARE_MEDICAID]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      // TODO(86b2rrua5): update cobranding logo to the MyCare Ohio-specific logo once we get it
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/buckeye_health_plan.png',
      width: 268,
      height: 32,
    },
    displayName: 'Buckeye Health Plan-MyCare Ohio (Medicare-Medicaid Plan)',
    fullBrandDisplayNameForMessaging: 'Buckeye Health Plan-MyCare Ohio (Medicare-Medicaid Plan)',
    shortBrandDisplayNameForMessaging: 'Buckeye Health Plan-MyCare Ohio (Medicare-Medicaid Plan)',
    brandCodes: new Set(['H0022']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    referralVerificationPageUrlSlug: 'buckeye-mycare',
    partnerOrganizations: [PartnerOrganization.CENTENEOH], // This brand is only relevant for the Centene OH Duals population
    referralLandingPageUrlSlugs: { en: 'buckeye-mycare', es: '' },
    referralLandingPageShortLinks: { en: BUCKEYE_MYCARE_LANDING_PAGE_SHORT_LINK_EN, es: '' },
    emailConfig: {
      // TODO(86b2rrua5): update cobranding logo to the MyCare Ohio-specific logo once we get it
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/buckeye_health_plan.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'H0022_MMP_149491E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'H0022_MMP_149492E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'H0022_MMP_149493E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'H0022_MMP_149494E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'H0022_MMP_149495E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: 'H0022_MMP_149496E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'H0022_MMP_149497E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
    },
  },

  [BrandName.AMBETTER]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/ambetter.png',
      width: 268,
      height: 32,
    },
    displayName: 'Ambetter from NH Healthy Families',
    fullBrandDisplayNameForMessaging: 'Ambetter from NH Healthy Families',
    shortBrandDisplayNameForMessaging: 'Ambetter Health',
    brandCodes: new Set(['75841']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/centene/cobranding64h/ambetter.png',
      tagline: '',
    },
    referralLandingPageUrlSlugs: { en: 'ambetter-nh', es: '' },
    referralVerificationPageUrlSlug: 'ambetter-nh',
    referralLandingPageShortLinks: {
      en: AMBETTER_NEWHAMPSHIRE_HEALTHY_FAMILIES_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: CENTENE_PARTNER_ORGANIZATIONS,
  },

  [BrandName.AMBETTER_OHIO_BUCKEYE_HEALTH_PLAN]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/centene/cobranding64h/ambetter_ohio.png',
      width: 268,
      height: 32,
    },
    displayName: 'Ambetter from Buckeye Health Plan',
    fullBrandDisplayNameForMessaging: 'Ambetter from Buckeye Health Plan',
    shortBrandDisplayNameForMessaging: 'Ambetter',
    brandCodes: new Set(['ABHPDEFAULT']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/ambetter_ohio.png',
      tagline: '',
    },
    referralLandingPageUrlSlugs: { en: 'ambetter-oh', es: '' },
    referralVerificationPageUrlSlug: 'ambetter-oh',
    referralLandingPageShortLinks: {
      en: AMBETTER_OHIO_BUCKEYE_HEALTH_PLAN_LANDING_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.CENTENEOH],
  },

  [BrandName.SUPERIOR_HEALTH_PLAN_STAR_PLUS_MMP]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.CENTENE,
    cobrandingLogo: {
      uri:
        'https://app.sprinterhealth.com/centene/cobranding64h/superior_health_plan_star_plus_mmp.png',
      width: 254,
      height: 32,
    },
    displayName: 'Superior HealthPlan STAR+PLUS Medicare-Medicaid Plan (MMP)',
    shortBrandDisplayNameForMessaging: 'Superior STAR+PLUS MMP',
    fullBrandDisplayNameForMessaging: 'Superior HealthPlan STAR+PLUS Medicare-Medicaid Plan (MMP)',
    brandCodes: new Set(['H6870']), // These gap lists do not contain PBP
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl:
        'https://app.sprinterhealth.com/centene/cobranding64h/superior_health_plan_star_plus_mmp.png',
      tagline: '',
      materialIDByEmailTypeMap: {
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT]: 'H6870_MMP_148783E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EASE]: 'H6870_MMP_148784E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_REWARDS]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_OUTCOMES]: 'H6870_MMP_148785E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_EMPATHETIC]: 'H6870_MMP_148786E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_HANDWRITTEN]: 'H6870_MMP_148787E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_TEASER]: null,
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_FOLLOW_UP_AUTHORITATIVE]:
          'H6870_MMP_148789E',
        [EmailType.PATIENT_CAMPAIGN_OUTREACH_INITIAL_ENGAGEMENT_END_OF_YEAR_REMINDER]: null,
      },
    },
    referralVerificationPageUrlSlug: 'superior-starplus-mmp',
    referralLandingPageUrlSlugs: { en: 'superior-starplus-mmp', es: '' },
    referralLandingPageShortLinks: {
      en: SUPERIOR_HEALTH_PLAN_STAR_PLUS_MMP_REFERRAL_VERIFICATION_PAGE_SHORT_LINK_EN,
      es: '',
    },
    partnerOrganizations: [PartnerOrganization.CENTENETX],
  },

  [BrandName.AETNA_BETTER_HEALTH_OF_ILLINOIS]: {
    partnerOrganizationGroup: PartnerOrganizationGroup.AETNA,
    cobrandingLogo: {
      uri: 'https://app.sprinterhealth.com/aetna/aetna_illinois.png',
      width: 264,
      height: 32,
    },
    displayName: 'Aetna Better Health of Illinois',
    fullBrandDisplayNameForMessaging: 'Aetna Better Health of Illinois',
    shortBrandDisplayNameForMessaging: 'Aetna',
    brandCodes: new Set(['SH-ABH-IL']),
    serviceSummaryCoverSheetConfig: { materialID: '', tagline: '' },
    emailConfig: {
      cobrandingBannerImageUrl: 'https://app.sprinterhealth.com/aetna/aetna_illinois.png',
      tagline: '',
    },
    referralVerificationPageUrlSlug: 'aetna-il',
    partnerOrganizations: [PartnerOrganization.AETNA],
  },
};

export default function getBrandConfig(brandName: BrandName): BrandConfig {
  return BRAND_CONFIG[brandName];
}

function normalizeBrandCode(brandCode: string): string {
  return brandCode
    .toUpperCase()
    .replace(/\s\s+/gm, ' ')
    .replace(/[^a-zA-Z0-9 -]+/g, '') // we want to allow hyphens to support brand codes with pbp numbers
    .trim();
}

export function getBrandConfigByBrandCode(
  brandCode: string | null | undefined,
): BrandConfig | null {
  if (brandCode == null) return null;

  const normalizedBrandCode = normalizeBrandCode(brandCode);

  const matchingBrandConfigs = Object.values(BRAND_CONFIG).filter((brandConfig) =>
    brandConfig.brandCodes.has(normalizedBrandCode),
  );

  // If no brand codes are found, we'll return null, since we don't want to throw an error.
  // Not all partner orgs have brand codes.
  if (matchingBrandConfigs.length === 0) {
    return null;
  }

  if (matchingBrandConfigs.length > 1) {
    throw new Error(`Duplicate brand codes found for brand code: ${brandCode}`);
  }

  return matchingBrandConfigs[0];
}

export function getBrandNameByBrandCode(brandCode: string | null | undefined): BrandName | null {
  if (brandCode == null) return null;

  const normalizedBrandCode = normalizeBrandCode(brandCode);

  const matchingBrandNames = Object.keys(BrandName).filter((brandName) =>
    BRAND_CONFIG[brandName as BrandName].brandCodes.has(normalizedBrandCode),
  );

  if (matchingBrandNames.length === 0) {
    return null;
  }

  if (matchingBrandNames.length > 1) {
    throw new Error(`Duplicate brand codes found for brand code: ${brandCode}`);
  }

  return matchingBrandNames[0] as BrandName;
}

export function getBrandCodesForPartnerOrganization(
  partnerOrganization: PartnerOrganization,
): string[] {
  return Object.keys(BrandName).flatMap((brandName) => {
    const brandConfig = BRAND_CONFIG[brandName as BrandName];
    if (!brandConfig.partnerOrganizations.includes(partnerOrganization)) {
      return [];
    }
    return [...brandConfig.brandCodes];
  });
}

/**
 * Returns all brand names for a given partner organization.
 *
 * If we want to apply the default brand code override (e.g. in validation for branded CMS content),
 * and a default brand code is specified for the campaign, only return the matching brand name.
 *
 * Else, return all brands for the partner organization.
 * @param campaignConfig - The campaign config for which to get the list of brands
 * @param shouldApplyDefaultBrandCodeOverride - Whether to apply the default brand code override
 * @returns All brand names for the given partner organization
 */
export function getBrandNamesForPartnerOrganization({
  campaignConfig,
  shouldApplyDefaultBrandCodeOverride = false,
}: {
  campaignConfig: CampaignConfig;
  shouldApplyDefaultBrandCodeOverride: boolean;
}): BrandName[] {
  const brandNames = Object.values(BrandName).filter((brandName) => {
    const brandConfig = BRAND_CONFIG[brandName];

    // Check if the brand is for the correct partner organization
    if (!brandConfig.partnerOrganizations.includes(campaignConfig.partnerOrganization)) {
      return false;
    }

    // If a defaultBrandCode is specified for the campaign, only return the matching brand
    if (shouldApplyDefaultBrandCodeOverride && campaignConfig.defaultBrandCode != null) {
      return brandConfig.brandCodes.has(campaignConfig.defaultBrandCode);
    }
    return true;
  });
  return brandNames;
}

/**
 * Returns all brand names for a given campaign, handling for the default brand code override as needed.
 *
 * @param campaignName - The campaign name for which to get the list of brands
 * @param shouldApplyDefaultBrandCodeOverride - Whether to apply the default brand code override
 * @returns All brand names for the given campaign
 */
export function getBrandNamesForCampaign({
  campaignName,
  shouldApplyDefaultBrandCodeOverride = false,
}: {
  campaignName: CampaignName | null;
  shouldApplyDefaultBrandCodeOverride: boolean;
}): BrandName[] {
  if (campaignName == null) return [];

  const campaignConfig = getCampaignConfig(campaignName);
  if (campaignConfig == null) return [];

  return getBrandNamesForPartnerOrganization({
    campaignConfig,
    shouldApplyDefaultBrandCodeOverride,
  });
}
